import { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';

import NotificationList from './notification.list.component';
import useNotifications from '../../hooks/notifications/useNotifications';
import history from '../../providers/history.provider';

const toInternalRoute = (link) => {
    try {
        const url = new URL(link, window.location.origin);
        return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
        return null;
    }
}

export default function NotificationBell() {

    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    const {
        items,
        pendingCount,
        hasMore,
        status,
        loadingMore,
        loadMoreFailed,
        loadMore,
        markAllRead,
        markAsRead,
        clearAll,
        retry,
    } = useNotifications();

    const toggleOpen = () => {

        const isOpening = !open;
        setOpen(isOpening);

        if (isOpening && pendingCount > 0)
            markAllRead();
    }

    useEffect(() => {

        const handleOutsideClick = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target))
                setOpen(false);
        }

        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    const handleItemClick = (notification) => {

        markAsRead(notification.id);

        const route = notification.additionalData?.link
            ? toInternalRoute(notification.additionalData.link)
            : null;

        if (!route)
            return;

        setOpen(false);
        history.push(route);
    }

    return (
        <div ref={containerRef} className="relative flex">
            <button
                type="button"
                aria-label="Notificações"
                aria-haspopup="true"
                aria-expanded={open}
                onClick={toggleOpen}
                className="relative flex items-center justify-center p-1 text-inherit"
            >
                <Bell className="w-6 h-6" />
                {pendingCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-lg bg-[#ff4d4f] text-white text-[10px] font-bold leading-none flex items-center justify-center">
                        {pendingCount > 99 ? '99+' : pendingCount}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute top-[calc(100%+14px)] right-0 z-[1200] w-[344px] max-w-[calc(100vw-16px)] rounded-[10px] border border-[#ededed] bg-white text-[#232323] shadow-[0_14px_40px_rgba(16,24,40,.18)]">
                    <div className="flex items-center justify-between gap-2 border-b border-[#ededed] px-4 py-[13px]">
                        <span className="text-[13px] font-semibold">Notificações</span>
                        <button
                            type="button"
                            onClick={clearAll}
                            disabled={items.length === 0}
                            title={items.length === 0 ? 'Nenhuma notificação para limpar' : undefined}
                            className="text-[12px] font-semibold text-primary hover:underline disabled:text-black/[.26] disabled:no-underline disabled:cursor-not-allowed"
                        >
                            Limpar todas
                        </button>
                    </div>

                    <NotificationList
                        items={items}
                        status={status}
                        loadingMore={loadingMore}
                        loadMoreFailed={loadMoreFailed}
                        hasMore={hasMore}
                        onLoadMore={loadMore}
                        onRetry={retry}
                        onItemClick={handleItemClick}
                    />
                </div>
            )}
        </div>
    );
}
